import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listLocations, listOccupiedLocationIds,
  upsertLocation, importLocationsCsv,
  startCycleCountForLocations,
} from "@/lib/inventory-catalog.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, Pencil, Upload, ClipboardCheck, MapPin } from "lucide-react";
import { toast } from "sonner";
import { CsvImportDialog } from "@/components/csv-import-dialog";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import { FilterBar } from "@/components/ui/filter-bar";
import { DataTable, type ColumnDef } from "@/components/ui/data-table";
import { StatusPill } from "@/components/ui/status-pill";
import type { Database } from "@/integrations/supabase/types";

type LocType = Database["public"]["Enums"]["location_type"];
const TYPES: LocType[] = ["receiving", "storage", "staging", "shipping", "damaged"];

type Loc = { id: string; code: string; type: LocType; zone: string | null; aisle: string | null; bay: string | null; level: string | null; pick_sequence: number | null; pickable: boolean; active: boolean };

export const Route = createFileRoute("/_authenticated/app/inventory/locations")({ component: LocationsPage });

function LocationsPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const listLocationsFn = useServerFn(listLocations);
  const listOccupiedFn = useServerFn(listOccupiedLocationIds);
  const upsertLocationFn = useServerFn(upsertLocation);
  const importLocationsFn = useServerFn(importLocationsCsv);
  const startCountFn = useServerFn(startCycleCountForLocations);
  const { tenantId, facilityId } = useScope();

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Loc | null>(null);
  const [code, setCode] = useState(""); const [type, setType] = useState<LocType>("storage");
  const [zone, setZone] = useState(""); const [aisle, setAisle] = useState(""); const [active, setActive] = useState(true);
  const [bay, setBay] = useState(""); const [level, setLevel] = useState(""); const [pickSeq, setPickSeq] = useState("");
  const [pickable, setPickable] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [starting, setStarting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["locations", facilityId],
    enabled: !!facilityId,
    queryFn: async () => (await listLocationsFn({ data: { facilityId: facilityId! } })) as Loc[],
    placeholderData: keepPreviousData,
  });

  const { data: occupiedIds } = useQuery({
    queryKey: ["loc-occupied", facilityId, tenantId],
    enabled: !!facilityId && !!tenantId,
    queryFn: () => listOccupiedFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const occupiedSet = useMemo(() => new Set(occupiedIds ?? []), [occupiedIds]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const s = search.trim().toLowerCase();
    return data.filter((l) => {
      if (typeFilter !== "all" && l.type !== typeFilter) return false;
      if (!s) return true;
      return l.code.toLowerCase().includes(s)
        || (l.zone ?? "").toLowerCase().includes(s)
        || (l.aisle ?? "").toLowerCase().includes(s);
    });
  }, [data, search, typeFilter]);

  const openCreate = () => { setEditing(null); setCode(""); setType("storage"); setZone(""); setAisle(""); setBay(""); setLevel(""); setPickSeq(""); setPickable(true); setActive(true); setOpen(true); };
  const openEdit = (l: Loc) => { setEditing(l); setCode(l.code); setType(l.type); setZone(l.zone ?? ""); setAisle(l.aisle ?? ""); setBay(l.bay ?? ""); setLevel(l.level ?? ""); setPickSeq(l.pick_sequence != null ? String(l.pick_sequence) : ""); setPickable(l.pickable ?? true); setActive(l.active); setOpen(true); };

  const togglePickable = async (l: Loc) => {
    if (!tenantId || !facilityId) return;
    try {
      await runServerFn(
        () => upsertLocationFn({ data: { id: l.id, tenantId, facilityId, payload: {
          code: l.code, type: l.type,
          zone: l.zone, aisle: l.aisle, bay: l.bay, level: l.level,
          pick_sequence: l.pick_sequence, pickable: !(l.pickable ?? true), active: l.active,
        } } }),
        "Toggle pickable",
      );
      toast.success(`${l.code} is now ${!(l.pickable ?? true) ? "pickable" : "non-pick"}`);
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch { /* toast handled */ }
  };

  const startCount = async () => {
    if (!tenantId || !facilityId) return;
    const ids = filtered.filter((l) => l.active).map((l) => l.id);
    if (ids.length === 0) return toast.error("No active locations to count");
    setStarting(true);
    try {
      const name = `Locations count ${new Date().toLocaleDateString()}`;
      const res = await runServerFn(
        () => startCountFn({ data: { tenantId, facilityId, name, locationIds: ids } }),
        "Create cycle count",
      );
      toast.success("Cycle count created");
      navigate({ to: "/app/cycle-counts/$id", params: { id: res.id } });
    } catch { /* toast handled */ } finally { setStarting(false); }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !facilityId) return toast.error("Pick a tenant and facility");
    setSubmitting(true);
    const seq = pickSeq.trim() ? Number(pickSeq) : null;
    const payload = {
      code: code.trim().toUpperCase(), type,
      zone: zone.trim() || null, aisle: aisle.trim() || null,
      bay: bay.trim() || null, level: level.trim() || null,
      pick_sequence: seq != null && Number.isFinite(seq) ? seq : null,
      pickable, active,
    };
    try {
      await runServerFn(
        () => upsertLocationFn({ data: { id: editing?.id, tenantId, facilityId, payload } }),
        editing ? "Update location" : "Create location",
      );
      toast.success(editing ? "Location updated" : "Location created");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["locations"] });
    } catch { /* toast handled */ } finally { setSubmitting(false); }
  };

  const cols: ColumnDef<Loc, unknown>[] = [
    { id: "code", header: "Code", accessorKey: "code", cell: ({ row }) => <span className="font-mono text-sm font-medium">{row.original.code}</span> },
    { id: "type", header: "Type", accessorKey: "type", cell: ({ row }) => <StatusPill tone="neutral">{row.original.type}</StatusPill> },
    { id: "zone", header: "Zone", cell: ({ row }) => <span className="text-muted-foreground text-sm">{row.original.zone ?? "—"}</span> },
    { id: "aisle", header: "Aisle", cell: ({ row }) => <span className="text-muted-foreground text-sm">{row.original.aisle ?? "—"}</span> },
    { id: "stock", header: "Stock", cell: ({ row }) => occupiedSet?.has(row.original.id) ? <StatusPill tone="info" dot>Occupied</StatusPill> : <StatusPill tone="neutral">Empty</StatusPill> },
    { id: "pickable", header: "Pickable", cell: ({ row }) => (
      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Switch checked={row.original.pickable ?? true} onCheckedChange={() => togglePickable(row.original)} />
        <span className="text-xs text-muted-foreground">{row.original.pickable ?? true ? "Pick" : "No-pick"}</span>
      </div>
    ) },
    { id: "active", header: "Active", cell: ({ row }) => <StatusPill tone={row.original.active ? "success" : "neutral"} dot>{row.original.active ? "Active" : "Inactive"}</StatusPill> },
    { id: "edit", header: "", enableSorting: false, cell: ({ row }) => <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}><Pencil className="h-4 w-4" /></Button> },
  ];

  return (
    <>
      <PageHeader
        title="Locations"
        description="Bins inside the selected facility."
        actions={
          <>
            {tenantId ? (
              <ExportMenu
                entity="locations"
                tenantId={tenantId}
                facilityId={facilityId}
                getRows={() => filtered.map((l) => ({
                  code: l.code,
                  type: l.type,
                  zone: l.zone ?? "",
                  aisle: l.aisle ?? "",
                  bay: (l as any).bay ?? "",
                  level: (l as any).level ?? "",
                  pick_sequence: (l as any).pick_sequence ?? "",
                  pickable: (l as any).pickable ?? true,
                  active: (l as any).active ?? true,
                }))}
                columns={["code","type","zone","aisle","bay","level","pick_sequence","pickable","active"]}
                filters={{ search, typeFilter }}
                disabled={filtered.length === 0}
              />
            ) : null}
            <Button size="sm" variant="outline" onClick={startCount} disabled={!facilityId || starting}>
              {starting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
              Cycle count
            </Button>
            <CsvImportDialog
              trigger={<Button size="sm" variant="outline" disabled={!facilityId}><Upload className="h-4 w-4 mr-2" />Import CSV</Button>}
              title="Import locations"
              templateHeaders={["code", "type", "zone", "aisle"]}
              sampleRow={["A-01-01", "storage", "A", "01"]}
              validateRow={(row) => {
                const code = (row.code ?? "").trim().toUpperCase();
                if (!code) return { ok: false, error: "code is required" };
                const t = ((row.type ?? "storage").trim().toLowerCase()) as LocType;
                if (!TYPES.includes(t)) return { ok: false, error: `invalid type: ${row.type}` };
                return { ok: true, value: { code, type: t, zone: (row.zone ?? "").trim() || null, aisle: (row.aisle ?? "").trim() || null } };
              }}
              onImport={async (rows) => {
                try {
                  return await importLocationsFn({ data: {
                    tenantId: tenantId!, facilityId: facilityId!,
                    rows: rows as { code: string; type: LocType; zone: string | null; aisle: string | null }[],
                  } });
                } catch (e) {
                  return { inserted: 0, failed: [{ row: 0, error: (e as Error).message }] };
                }
              }}
              onDone={() => qc.invalidateQueries({ queryKey: ["locations"] })}
            />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button size="sm" onClick={openCreate} disabled={!facilityId}><Plus className="h-4 w-4 mr-2" />New location</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editing ? "Edit location" : "Create location"}</DialogTitle></DialogHeader>
                <form onSubmit={onSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2"><Label>Code</Label><Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="A-01-01" /></div>
                    <div className="space-y-2"><Label>Type</Label>
                      <Select value={type} onValueChange={(v) => setType(v as LocType)}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2"><Label>Zone</Label><Input value={zone} onChange={(e) => setZone(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Aisle</Label><Input value={aisle} onChange={(e) => setAisle(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Bay</Label><Input value={bay} onChange={(e) => setBay(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Level</Label><Input value={level} onChange={(e) => setLevel(e.target.value)} /></div>
                    <div className="space-y-2"><Label>Pick sequence</Label><Input type="number" value={pickSeq} onChange={(e) => setPickSeq(e.target.value)} placeholder="lower = picked first" /></div>
                  </div>
                  <div className="flex items-center justify-between rounded-md border px-3 py-2">
                    <div>
                      <Label className="text-sm">Pickable</Label>
                      <p className="text-xs text-muted-foreground">Off = reserve/overflow bin, skipped by pick paths.</p>
                    </div>
                    <Switch checked={pickable} onCheckedChange={setPickable} />
                  </div>
                  {editing && (
                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                      <div><Label className="text-sm">Active</Label></div>
                      <Switch checked={active} onCheckedChange={setActive} />
                    </div>
                  )}
                  <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? "Save" : "Create"}</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </>
        }
      />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <ContextualTip tipKey="inventory_locations_intro" />
        <FilterBar search={search} onSearchChange={setSearch} searchPlaceholder="Search code, zone, aisle…">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </FilterBar>
        <DataTable
          data={filtered}
          columns={cols}
          isLoading={isLoading}
          getRowId={(l) => l.id}
          onRowClick={(l) => navigate({ to: "/app/inventory/locations/$locationId", params: { locationId: l.id } })}
          empty={{ title: data && data.length > 0 ? "No matches" : "No locations yet", description: data && data.length > 0 ? "Adjust your filters." : "Create bins or import a CSV to start receiving inventory." }}
          mobileCard={(l) => (
            <div className="space-y-1.5" onClick={() => navigate({ to: "/app/inventory/locations/$locationId", params: { locationId: l.id } })}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-sm flex items-center gap-1.5"><MapPin className="h-3 w-3 text-muted-foreground" />{l.code}</span>
                <div className="flex items-center gap-1">
                  {occupiedSet?.has(l.id) && <StatusPill tone="info">Occupied</StatusPill>}
                  <StatusPill tone={l.active ? "success" : "neutral"} dot>{l.active ? "On" : "Off"}</StatusPill>
                </div>
              </div>
              <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                <span>{l.type}</span>
                {l.zone && <span>Zone {l.zone}</span>}
                {l.aisle && <span>Aisle {l.aisle}</span>}
              </div>
            </div>
          )}
        />
      </div>
    </>
  );
}
